import { ReactNode, Ref } from 'react';
import { cn } from '../../utils/className';

interface MasterDetailProps {
  master: ReactNode;
  /** Without a detail, the master takes the full width. */
  detail?: ReactNode;
  /** Width of the master pane in pixels. */
  masterWidth?: number;
  masterRef?: Ref<HTMLElement>;
  className?: string;
}

/**
 * A list beside its detail. Both panes stay live, so neither traps focus. The
 * master keeps its place in the tree when the detail opens or closes, so the
 * list does not remount and keeps its scroll position.
 */
export function MasterDetail({
  master,
  detail,
  masterWidth = 380,
  masterRef,
  className,
}: MasterDetailProps) {
  const hasDetail = detail != null;

  return (
    <div
      data-master-detail={hasDetail ? '' : undefined}
      className={cn('flex min-h-0 min-w-0 flex-1', className)}
    >
      <section
        ref={masterRef}
        className={cn(
          'flex min-h-0 flex-col overflow-hidden',
          hasDetail ? 'border-border-primary border-r' : 'min-w-0 flex-1'
        )}
        style={hasDetail ? { width: masterWidth } : undefined}
      >
        {master}
      </section>

      {hasDetail && (
        <aside
          data-master-detail-pane
          aria-label='Machine details'
          className='bg-surface-primary flex min-h-0 min-w-0 flex-1 flex-col
            overflow-hidden'
        >
          {detail}
        </aside>
      )}
    </div>
  );
}
